import { memo, useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { ModalBody, ModalContent, Modal, ModalHeader, ModalFooter } from '@heroui/react';
import { CalendarDate } from '@internationalized/date';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useUpdateTermMutation, Term, type TermFreeRange } from '@/entities/Courses';
import { AppButton } from '@/shared/ui/AppButton';
import { CustomInput, CustomDatepicker, FieldHint } from '@/shared/ui/Fields';
import { scaleInOut } from '@/shared/lib/utils/modalTransitions';
import { useToast } from '@/shared/lib/hooks/useToast';
import {
  findNeighbourNumbers,
  getOutOfTermMessage,
  getScheduledRangeWarning,
  useTermDatePickerProps,
  useTermFormDates,
  testTermEndDate,
  testTermNumberFree,
  testTermStartDate,
  toCalendarDate,
  toDateOnly,
  type TermDatesContext,
} from '../../../lib/termDates';
import styles from './EditTermModal.module.scss';

interface EditTermModalProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  intakeId: number;
  term: Term | null;
  // Numbers of the intake terms and the free periods: a changed number moves the term between others
  termNumbers?: number[];
  freeRanges?: TermFreeRange[];
  // Numbers of the other terms of the course: the number can't be changed to one of them
  takenNumbers?: number[];
  onSuccess?: () => void;
}

interface TermFormData {
  number: number;
  startDate: CalendarDate | null;
  endDate: CalendarDate | null;
}

const termSchema = yup.object({
  number: yup
    .number()
    .required('Term number is required')
    .min(1)
    .integer()
    .test('number-free', '', testTermNumberFree),
  startDate: yup
    .mixed()
    .required('Start date is required')
    .test('within-bounds', '', testTermStartDate),
  endDate: yup.mixed().required('End date is required').test('within-bounds', '', testTermEndDate),
}) as yup.ObjectSchema<TermFormData>;

export const EditTermModal = memo(
  ({
    isOpen,
    onOpenChange,
    intakeId,
    term,
    termNumbers = [],
    freeRanges,
    takenNumbers,
    onSuccess,
  }: EditTermModalProps) => {
    const { toast } = useToast();
    const [updateTerm, { isLoading }] = useUpdateTermMutation();
    // The typed number picks the bounds: between the same neighbours — the term's own, between
    // other terms — that free period of the intake
    const [termNumber, setTermNumber] = useState<number | null>(term?.number ?? null);
    const {
      ranges,
      blocked,
      hint: datesHint,
    } = useTermFormDates(
      termNumber,
      freeRanges,
      term
        ? { dateBounds: term.dateBounds, ...findNeighbourNumbers(termNumbers, term.number) }
        : undefined,
      takenNumbers,
    );

    const {
      control,
      handleSubmit,
      formState: { errors },
      reset,
      watch,
    } = useForm<TermFormData, TermDatesContext>({
      resolver: yupResolver(termSchema) as any,
      context: { ranges, blockedMessage: blocked, takenNumbers: takenNumbers ?? [] },
      defaultValues: {
        number: 0,
        startDate: null,
        endDate: null,
      },
    });

    const startDatePickerProps = useTermDatePickerProps(ranges);
    // The end stays in the period of the chosen start
    const endDatePickerProps = useTermDatePickerProps(ranges, watch('startDate'));

    // Narrowing is allowed: the admin is only told what will be left outside the term
    const scheduledWarning = getScheduledRangeWarning(
      term?.scheduledRange,
      watch('startDate'),
      watch('endDate'),
    );

    useEffect(() => {
      if (term && isOpen) {
        // YYYY-MM-DD as is: new Date() would read it as UTC and shift it west of UTC
        const startDate = toCalendarDate(term.startDate);
        const endDate = toCalendarDate(term.endDate);
        reset({
          number: term.number || 0,
          startDate: startDate || null,
          endDate: endDate || null,
        });
        setTermNumber(term.number || null);
      }
    }, [term, isOpen, reset]);

    const onSubmit = async (data: TermFormData) => {
      if (!data.startDate || !data.endDate || !term) {
        return;
      }

      try {
        const result = await updateTerm({
          intakeId,
          termNumber: term.number,
          body: {
            number: data.number,
            startDate: toDateOnly(data.startDate),
            endDate: toDateOnly(data.endDate),
          },
        }).unwrap();

        const outOfTermItems = result.data?.outOfTermItems ?? [];
        toast(
          outOfTermItems.length > 0
            ? {
                title: 'Term updated',
                description: getOutOfTermMessage(outOfTermItems),
                color: 'warning',
              }
            : {
                title: 'Success',
                description: result.message || 'Term updated successfully',
                color: 'success',
              },
        );

        handleClose();
        onSuccess?.();
      } catch (error: any) {
        toast({
          title: 'Error',
          description: error?.data?.message || 'Failed to update term',
          color: 'danger',
        });
      }
    };

    const handleClose = () => {
      reset({
        number: 0,
        startDate: null,
        endDate: null,
      });
      onOpenChange(false);
    };

    if (!term) return null;

    return (
      <Modal
        isOpen={isOpen}
        onOpenChange={handleClose}
        placement="center"
        motionProps={scaleInOut}
        size="2xl"
        classNames={{
          base: 'p-6 flex flex-col gap-5 max-w-[600px]',
          closeButton: styles.closeBtn,
        }}
      >
        <ModalContent>
          {() => (
            <form onSubmit={handleSubmit(onSubmit as any)}>
              <ModalHeader className={styles.title}>Edit term</ModalHeader>
              <ModalBody className="p-0 flex flex-col gap-6">
                <Controller
                  name="number"
                  control={control}
                  render={({ field }) => (
                    <CustomInput
                      {...field}
                      value={field.value?.toString() || ''}
                      onChange={(e) => {
                        const number = Number(e.target.value) || 0;
                        field.onChange(number);
                        setTermNumber(number || null);
                      }}
                      type="number"
                      label="Term Number"
                      labelPlacement="outside"
                      placeholder="Enter term number"
                      errorMessage={errors.number?.message}
                      showError={!!errors.number}
                      isRequired
                    />
                  )}
                />

                <div className="flex flex-col gap-2">
                  <div className="flex gap-4">
                    <Controller
                      name="startDate"
                      control={control}
                      render={({ field }) => (
                        <CustomDatepicker
                          label="Start date"
                          labelPlacement="outside"
                          value={field.value}
                          onChange={field.onChange}
                          errorMessage={errors.startDate?.message}
                          showError={!!errors.startDate}
                          isRequired
                          {...startDatePickerProps}
                          placeholderValue={null}
                          className="flex-1"
                        />
                      )}
                    />

                    <Controller
                      name="endDate"
                      control={control}
                      render={({ field }) => (
                        <CustomDatepicker
                          label="End date"
                          labelPlacement="outside"
                          value={field.value}
                          onChange={field.onChange}
                          errorMessage={errors.endDate?.message}
                          showError={!!errors.endDate}
                          isRequired
                          {...endDatePickerProps}
                          placeholderValue={null}
                          className="flex-1"
                        />
                      )}
                    />
                  </div>
                  {/* The available dates apply to both pickers */}
                  <FieldHint>{datesHint}</FieldHint>
                </div>
                {scheduledWarning && (
                  <p className="text-sm text-yellow-tertiary" role="status">
                    {scheduledWarning}
                  </p>
                )}
              </ModalBody>
              <ModalFooter className="gap-4 pt-6 px-0">
                <AppButton fullWidth color="white" onPress={handleClose} type="button">
                  Cancel
                </AppButton>
                <AppButton
                  fullWidth
                  color="black"
                  type="submit"
                  isLoading={isLoading}
                  onClick={(e) => {
                    e.preventDefault();
                    handleSubmit(onSubmit as any)();
                  }}
                >
                  Save changes
                </AppButton>
              </ModalFooter>
            </form>
          )}
        </ModalContent>
      </Modal>
    );
  },
);

EditTermModal.displayName = 'EditTermModal';
